import { useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useLang, LANGS } from "@/lib/i18n";
import { TOKEN_CONFIG } from "@/lib/config";
import Icon from "@/components/shared/Icon";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const NAV = [
  { to: "/", key: "nav.home", end: true },
  { to: "/learn", key: "nav.learn" },
  { to: "/play", key: "nav.play" },
  { to: "/stories", key: "nav.stories" },
  { to: "/impact", key: "nav.impact" },
  { to: "/about", key: "nav.about" },
  { to: "/token", key: "nav.token" },
];

function LangSwitch({ onNavigate }) {
  const { param, t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const switchTo = (target) => {
    const rest = location.pathname.replace(/^\/[^/]+/, "");
    navigate(`/${target}${rest}${location.search}`);
    onNavigate?.();
  };

  return (
    <div
      className="inline-flex items-center rounded-full border-2 border-[#147DEB]/20 bg-white p-0.5"
      role="group"
      aria-label={t("nav.language")}
    >
      {LANGS.map((l) => (
        <button
          key={l.param}
          onClick={() => switchTo(l.param)}
          data-testid={`lang-switch-${l.param}`}
          aria-pressed={param === l.param}
          className={`rounded-full px-3 py-1.5 text-sm font-bold transition-colors ${
            param === l.param ? "bg-[#147DEB] text-white" : "text-[#082B66] hover:bg-[#E8F5FF]"
          }`}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const { t, lp } = useLang();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `relative px-3 py-2 text-sm font-bold transition-colors rounded-xl ${
      isActive ? "text-[#147DEB]" : "text-[#082B66] hover:text-[#147DEB] hover:bg-[#E8F5FF]"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-[#147DEB]/10 bg-[#FFFCF5]/90 shadow-sm backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-3 sm:px-6 lg:px-8" aria-label="Main">
        <Link to={lp("/")} className="flex items-center gap-2.5" data-testid="nav-logo-link">
          <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-[#147DEB]/15">
            <img src="/assets/lumora-logo.jpeg" alt="LUMORA logo" className="h-full w-full object-contain" />
          </span>
          <span className="font-display text-2xl font-bold tracking-tight text-[#082B66]">LUMORA</span>
        </Link>

        <div className="hidden items-center gap-0.5 lg:flex">
          {NAV.map((n) => (
            <NavLink key={n.key} to={lp(n.to)} end={n.end} className={linkClass} data-testid={`nav-${n.key.split(".")[1]}-link`}>
              {t(n.key)}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <LangSwitch />
          <a
            href={TOKEN_CONFIG.buyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-buy-token-link"
            className="inline-flex items-center gap-1.5 rounded-xl border-2 border-[#147DEB]/20 bg-white px-3.5 py-2 text-sm font-bold text-[#082B66] transition-colors hover:border-[#147DEB]"
          >
            <Icon name="Coins" className="h-4 w-4" />
            {t("nav.buyToken")}
          </a>
          <Link
            to={lp("/learn")}
            data-testid="nav-start-learning-cta"
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#147DEB] px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-[#147DEB]/25 transition-all hover:-translate-y-0.5 hover:bg-[#0F62BD]"
          >
            <Icon name="Rocket" className="h-4 w-4" />
            {t("nav.startLearning")}
          </Link>
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <LangSwitch />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="flex h-11 w-11 items-center justify-center rounded-xl border-2 border-[#147DEB]/20 bg-white text-[#082B66]"
                data-testid="mobile-menu-button"
                aria-label={t("nav.menu")}
              >
                <Icon name="Menu" className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-[#FFFCF5] p-0">
              <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
              <SheetDescription className="sr-only">LUMORA navigation</SheetDescription>
              <div className="flex items-center gap-2.5 border-b border-[#147DEB]/10 p-4">
                <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-[#147DEB]/15">
                  <img src="/assets/lumora-logo.jpeg" alt="LUMORA logo" className="h-full w-full object-contain" />
                </span>
                <span className="font-display text-xl font-bold text-[#082B66]">LUMORA</span>
              </div>
              <div className="flex flex-col gap-1 p-4">
                {NAV.concat([{ to: "/community", key: "nav.community" }, { to: "/faq", key: "nav.faq" }]).map((n) => (
                  <NavLink
                    key={n.key}
                    to={lp(n.to)}
                    end={n.end}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.key.split(".")[1]}-link`}
                    className={({ isActive }) =>
                      `rounded-xl px-4 py-3 text-base font-bold ${isActive ? "bg-[#E8F5FF] text-[#147DEB]" : "text-[#082B66]"}`
                    }
                  >
                    {t(n.key)}
                  </NavLink>
                ))}
                <Link
                  to={lp("/learn")}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-start-learning-cta"
                  className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#147DEB] px-4 py-3 text-base font-bold text-white"
                >
                  <Icon name="Rocket" className="h-5 w-5" />
                  {t("nav.startLearning")}
                </Link>
                <a
                  href={TOKEN_CONFIG.buyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-buy-token-link"
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border-2 border-[#147DEB]/20 bg-white px-4 py-3 text-base font-bold text-[#082B66]"
                >
                  <Icon name="Coins" className="h-5 w-5" />
                  {t("nav.buyToken")}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
